import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { aiGradientTextSx } from 'common/constants/ai';
import {
  BOOLEAN_FIELD_ALIASES,
  DIVIDEND_PAYMENT_TYPE_ALIAS,
  SECURITY_TYPE_ALIAS,
} from 'common/constants/cap-table/capTable';
import { PERCENT, PERCENTAGE } from 'common/constants/general';
import { DIVIDEND_PAYMENT_CASH, DIVIDEND_PAYMENT_PIK } from 'common/constants/inputs';
import { LIST as SECURITY_TYPE_LIST } from 'common/constants/securityTypes';
import { RowFormat } from 'common/types/scalarSpreadsheet';
import { DEFAULT_CURRENCY } from 'components/FeaturedSpreadsheet/constants';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { useDocumentFootnotes } from 'context/DocumentFootnotesContext';
import CitationService from 'services/citations';
import { useCellShowsMerge, useReviewDrawerOpen } from 'store/useReviewStore';
import { AIMergeValueViewerProps } from './AIMergeValueViewer.props';
import {
  getMergeBoxSx,
  mergeAIValueSx,
  mergeArrowSx,
  mergeCurrentValueSx,
  mergeTypographySx,
  restPurpleAnchorSx,
  restRevealAnchorSx,
} from './AIMergeValueViewer.styles';
import AISecurityFindingPopover from './AISecurityFindingPopover';
import FastValueViewer from '../../../../../components/ScalarSpreadsheet/utilities/FastValueViewer';
import { useAIFieldAcceptContext } from '../captable-table/AIFieldAcceptContext';

const YES_NO_LABELS = ['No', 'Yes'];

const formatDisplayValue = (
  val: unknown,
  dbType?: string,
  fieldAlias?: string,
  cellFormat?: RowFormat,
  gridType?: string,
  currencyCode?: string
): string => {
  if (val === null || val === undefined || val === '') {
    return '';
  }
  if (fieldAlias === SECURITY_TYPE_ALIAS) {
    const label = SECURITY_TYPE_LIST[Number(val) - 1];
    return label ?? String(val);
  }
  if (fieldAlias === DIVIDEND_PAYMENT_TYPE_ALIAS) {
    const normalized = typeof val === 'string' ? val.toLowerCase() : val;
    if (normalized === 0 || normalized === DIVIDEND_PAYMENT_CASH.toLowerCase()) {
      return DIVIDEND_PAYMENT_CASH;
    }
    if (normalized === 1 || normalized === DIVIDEND_PAYMENT_PIK.toLowerCase()) {
      return DIVIDEND_PAYMENT_PIK;
    }
  }
  if (dbType === 'boolean' || BOOLEAN_FIELD_ALIASES.has(fieldAlias ?? '')) {
    let idx: number;
    if (typeof val === 'boolean') {
      idx = val ? 1 : 0;
    } else {
      idx = Number(val);
    }
    return YES_NO_LABELS[idx] ?? String(val);
  }
  if (typeof val === 'boolean') {
    return val ? 'Yes' : 'No';
  }
  const num = Number(val);
  if (!Number.isNaN(num)) {
    if (cellFormat?.suffix) {
      const decimals = cellFormat.maximumFractionDigits ?? 0;
      return `${num.toFixed(decimals)}${cellFormat.suffix}`;
    }
    if (gridType === PERCENTAGE || cellFormat?.style === PERCENT) {
      const displayNum = cellFormat?.isDecimal !== false ? num * 100 : num;
      const decimals = cellFormat?.maximumFractionDigits ?? 2;
      return `${displayNum.toFixed(decimals)}%`;
    }
    if (cellFormat?.style === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode ?? DEFAULT_CURRENCY,
        minimumFractionDigits: cellFormat.minimumFractionDigits,
        maximumFractionDigits: cellFormat.maximumFractionDigits,
      }).format(num);
    }
    return num.toLocaleString('en-US', { maximumFractionDigits: 6 });
  }
  return String(val);
};

const FallbackValueViewer = ({
  value,
  originalValueViewer: OriginalViewer,
  restProps,
  onDoubleClick,
}: {
  value: string | number;
  originalValueViewer?: React.ComponentType<any>;
  restProps: Record<string, any>;
  onDoubleClick: (event: React.MouseEvent) => void;
}) => {
  if (OriginalViewer) {
    return <OriginalViewer value={value} {...restProps} onDoubleClick={onDoubleClick} />;
  }
  return <FastValueViewer value={value} {...restProps} onDoubleClick={onDoubleClick} />;
};

const AIMergeValueViewer = ({
  value,
  aiFieldMetadata,
  aiAccuracyConfidence,
  fieldLabel,
  securityId,
  fieldAlias,
  onRemoveFinding,
  originalValueViewer: OriginalValueViewer,
  ...restProps
}: AIMergeValueViewerProps) => {
  const [ctrlHeld, setCtrlHeld] = useState(false);
  const { acceptField, dismissField } = useAIFieldAcceptContext();
  const { format: spreadsheetFormat } = useContext(FeaturedSpreadsheetContext);
  const currencyCode = (spreadsheetFormat as any)?.currency?.code ?? DEFAULT_CURRENCY;
  const [, setLocalVersion] = useState(0);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        setCtrlHeld(true);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) {
        setCtrlHeld(false);
      }
    };
    const onBlur = () => setCtrlHeld(false);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);
  const { getDocumentMetadata } = useDocumentFootnotes();
  const citationService = useMemo(() => new CitationService(), []);

  const isAlreadyActioned = !!aiFieldMetadata.is_accepted;
  // A still-pending field PREVIEWS the AI value at rest; once settled
  // (accepted/dismissed) the cell falls back to its own value viewer.
  const isPending = aiFieldMetadata.status ? aiFieldMetadata.status === 'pending' : !aiFieldMetadata.is_accepted;

  const handleAccept = useCallback(() => {
    if (securityId && fieldAlias) {
      acceptField(securityId, fieldAlias);
    }
  }, [securityId, fieldAlias, acceptField]);

  const handleDismiss = useCallback(() => {
    if (securityId && fieldAlias) {
      dismissField(securityId, fieldAlias);
    }
  }, [securityId, fieldAlias, dismissField]);

  const cellOnDoubleClick = restProps.onDoubleClick as ((event: React.MouseEvent) => void) | undefined;
  const wrappedOnDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      if (event.ctrlKey || event.metaKey) {
        return;
      }
      cellOnDoubleClick?.(event);
    },
    [cellOnDoubleClick]
  );

  const pendingCitation = useMemo(
    () => restProps.cell?.citationReferences?.find((c: any) => c.is_accepted === false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [restProps.cell?.citationReferences]
  );

  const pendingCitationDocumentName = useMemo(() => {
    if (!pendingCitation) {
      return undefined;
    }
    return getDocumentMetadata(pendingCitation.document_reference)?.documentName;
  }, [pendingCitation, getDocumentMetadata]);

  const handleAcceptCitation = useCallback(() => {
    if (!pendingCitation?.id || !restProps.cell?.citationReferences) {
      return;
    }

    const citationId = pendingCitation.id;

    // Update local cell state immediately for instant UI feedback
    // eslint-disable-next-line no-param-reassign
    restProps.cell.citationReferences = restProps.cell.citationReferences.map((c: any) =>
      c.id === citationId ? { ...c, is_accepted: true } : c
    );
    setLocalVersion(v => v + 1);

    citationService.updateCitationAcceptance(citationId, true).catch(() => {
      // Revert on failure
      // eslint-disable-next-line no-param-reassign
      restProps.cell.citationReferences = restProps.cell.citationReferences?.map((c: any) =>
        c.id === citationId ? { ...c, is_accepted: false } : c
      );
      setLocalVersion(v => v + 1);
    });
  }, [pendingCitation, restProps.cell, citationService]);

  const aiValue = aiFieldMetadata.value;
  const cellValue = restProps.cell?.value;
  const dbType = restProps.cell?.dbType as string | undefined;
  const cellFormat = restProps.cell?.format as RowFormat | undefined;
  const gridType = restProps.cell?.gridType as string | undefined;
  const currentDisplay = useMemo(
    () => formatDisplayValue(cellValue ?? value, dbType, fieldAlias, cellFormat, gridType, currencyCode),
    [cellValue, value, dbType, fieldAlias, cellFormat, gridType, currencyCode]
  );
  const aiDisplay = useMemo(
    () => formatDisplayValue(aiValue, dbType, fieldAlias, cellFormat, gridType, currencyCode),
    [aiValue, dbType, fieldAlias, cellFormat, gridType, currencyCode]
  );
  const hasCurrentValue =
    (cellValue ?? value) !== null && (cellValue ?? value) !== undefined && (cellValue ?? value) !== '';
  const valuesDiffer = aiValue !== undefined && hasCurrentValue && currentDisplay !== aiDisplay;
  // SCAL-8976: the "current -> ai" purple merge text is hover/active-only now. It
  // shows only while this exact cell is the review card being hovered or the active
  // (selected) card. At rest the cell renders its normal value and stays editable.
  // The selector is per-cell, so only this viewer re-renders when its match flips.
  const showMerge = useCellShowsMerge(securityId, fieldAlias);
  const renderMerge = valuesDiffer && showMerge;
  // SCAL-9150: drawer-open IS the review context. A still-pending cell flags itself
  // AI-purple BY DEFAULT while the drawer is open (no card hovered/selected needed),
  // then reverts to normal colors when the drawer closes. This only recolors the
  // resting AI preview - the previous value is still revealed solely via the hover/
  // active merge text below, never at rest.
  const drawerOpen = useReviewDrawerOpen();
  const restIsPurple = isPending && drawerOpen && !renderMerge;
  const currentDisplayOrNA = currentDisplay || 'N/A';
  // Full, untruncated "current -> ai" text. Available whenever the values differ - not
  // just in the hovered/active merge state - so the same string powers both the merge
  // tooltip AND the rest-state reveal tooltip below (renderMerge implies valuesDiffer).
  const mergeTooltipText = valuesDiffer ? `${currentDisplayOrNA} → ${aiDisplay}` : '';

  // At rest, render through the cell's OWN value viewer so formatting, alignment and
  // padding stay single-sourced with every other cell (no bespoke layout here). A
  // pending field previews the AI value - fed in via a cell clone, since the viewers
  // read cell.value, not the value prop - while a settled field shows its real value.
  // Normal color either way; the AI gradient is reserved for the hovered merge view.
  const restContent = (
    <FallbackValueViewer
      value={(isPending ? aiValue : value) as string | number}
      originalValueViewer={OriginalValueViewer}
      restProps={
        isPending && restProps.cell
          ? { ...restProps, cell: { ...restProps.cell, value: aiValue } }
          : restProps
      }
      onDoubleClick={wrappedOnDoubleClick}
    />
  );

  // Even at rest (when the transient merge text is NOT shown), keep the full previous
  // value reachable: when the values differ, wrap the cell's own viewer in a reveal
  // Tooltip carrying the untruncated "current -> ai". The viewer may not forward a ref
  // or accept mouse events, so anchor the Tooltip on a ref-forwarding span that mirrors
  // the cell's layout (restRevealAnchorSx) - no change to alignment, width or padding.
  // While the drawer is open a pending cell uses the AI-purple variant of that anchor
  // (restPurpleAnchorSx) so its resting AI preview reads purple (SCAL-9150). Cells that
  // need neither a tooltip (values equal) nor the purple default render bare.
  const restAnchored =
    valuesDiffer || restIsPurple ? (
      <Box
        component="span"
        sx={restIsPurple ? restPurpleAnchorSx : restRevealAnchorSx}
        data-review-purple={restIsPurple || undefined}>
        {restContent}
      </Box>
    ) : (
      restContent
    );
  const restWithReveal = valuesDiffer ? (
    <Tooltip title={mergeTooltipText} placement="top" arrow>
      {restAnchored}
    </Tooltip>
  ) : (
    restAnchored
  );

  return (
    <AISecurityFindingPopover
      fieldMetadata={aiFieldMetadata}
      accuracyConfidence={aiAccuracyConfidence}
      fieldLabel={fieldLabel}
      onAccept={!isAlreadyActioned && securityId && fieldAlias ? handleAccept : undefined}
      // eslint-disable-next-line no-nested-ternary
      onRemove={!isAlreadyActioned ? (securityId && fieldAlias ? handleDismiss : onRemoveFinding) : undefined}
      pendingCitation={pendingCitation}
      onAcceptCitation={pendingCitation ? handleAcceptCitation : undefined}
      pendingCitationDocumentName={pendingCitationDocumentName}>
      {({ onClick }) => (
        <Box
          onMouseDownCapture={e => {
            // Intercept (and open the in-cell finding popover via onClickCapture)
            // only while the purple merge text is actually shown, or on ctrl/meta.
            // At rest the cell is plain and editable, so let the event fall through.
            if (renderMerge || e.ctrlKey || e.metaKey) {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation?.();
            }
          }}
          onClickCapture={e => {
            if (renderMerge || e.ctrlKey || e.metaKey) {
              onClick(e);
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation?.();
            }
          }}
          sx={getMergeBoxSx(renderMerge, ctrlHeld)}>
          {renderMerge ? (
            <Tooltip title={mergeTooltipText} placement="top" arrow>
              <Typography component="span" variant="body2" noWrap sx={mergeTypographySx}>
                {/* The "before" value stays neutral; only the AI value carries the
                    gradient so it is obvious which side is the AI suggestion. */}
                <Box component="span" sx={mergeCurrentValueSx}>
                  {currentDisplayOrNA}
                </Box>
                <Box component="span" sx={mergeArrowSx}>
                  →
                </Box>
                <Box component="span" sx={{ ...mergeAIValueSx, ...(aiGradientTextSx as object) }}>
                  {aiDisplay}
                </Box>
              </Typography>
            </Tooltip>
          ) : (
            restWithReveal
          )}
        </Box>
      )}
    </AISecurityFindingPopover>
  );
};

export default AIMergeValueViewer;
